// The Freaks helpers shared by the game view, the entry buttons, the profile card and Settings.
// Port of mobile utils/theFreaks.ts — keep both in sync.
import en from '../localization/en';
import pl from '../localization/pl';
import freaksEn from '../localization/theFreaks/en';
import freaksPl from '../localization/theFreaks/pl';

export const THE_FREAKS_LINK = '/the-freaks';
// Settings toggles (UserNotificationPreference context/type), ON unless turned off
export const FREAKS_PREFERENCE_CONTEXT = 'games';
export const FREAKS_DAILY_PREFERENCE = 'THE_FREAKS_DAILY';
export const FREAKS_CHALLENGE_PREFERENCE = 'THE_FREAKS_CHALLENGE';
export const FREAKS_INVITE_PREFERENCE = 'THE_FREAKS_INVITE';
// not a notification: shows my Freaks card in my profile Statistics (ON unless turned off)
export const FREAKS_PROFILE_PREFERENCE = 'THE_FREAKS_PROFILE';

const KNOWN_ERRORS = [
  'not_enough_energy', 'not_enough_cash', 'recovering', 'locked', 'already_owned', 'max_level', 'no_base',
  'no_refills_left', 'energy_full', 'not_recovering', 'target_protected', 'already_claimed', 'already_challenged',
  'busy', 'already_invited', 'not_friends', 'level_locked', 'newcomer_bots_only', 'target_newcomer',
  'quest_not_done', 'unknown_quest', 'no_repair_needed', 'safe_full', 'not_enough_in_safe', 'invalid_amount',
];

const AWARD_EMOJI = {
  season1: '🥇',
  season2: '🥈',
  season3: '🥉',
  seasonTop10: '🏅',
  activityChampion: '👑',
  dailyChampion: '🏆',
  streak30: '🔥',
  winStreak5: '🥊',
  winStreak10: '💥',
  missions100: '🎯',
  missions1000: '🚀',
  recruiter: '🤝',
};

/** Emoji for a medal / badge key (⭐ for ones this app doesn't know yet). */
export const awardEmoji = (key) => AWARD_EMOJI[key] ?? '⭐';

/** Translation key for a medal / badge name; unknown keys fall back to a generic name. */
export const awardNameKey = (key) =>
  AWARD_EMOJI[key] ? `theFreaks.awards.names.${key}` : 'theFreaks.awards.names.other';

const WORLD_EMOJI = {
  payday: '💰',
  luckyDay: '🍀',
  trainingCamp: '🏋️',
  fightNight: '🥊',
  boomingBusiness: '📈',
  hotActivity: '🔥',
};

/** Emoji and translation key of a world event; null for events this app doesn't know (not shown). */
export const worldEventInfo = (key) =>
  WORLD_EMOJI[key] ? { emoji: WORLD_EMOJI[key], key: `theFreaks.world.${key}` } : null;

/** How many runs "Repeat" offers for a mission: as many as the energy allows, up to the max (0 = no Repeat). */
export const repeatTimes = (energy, missionEnergy, max = 5) => {
  const times = Math.min(max, Math.floor(energy / Math.max(1, missionEnergy)));
  return times >= 2 ? times : 0;
};

/** The API rule code of a failed game action (superagent puts the body on error.response.body). */
export const freaksErrorCode = (error) =>
  error?.response?.body?.errors?.message ?? error?.response?.data?.errors?.message;

/** Translation key for a failed game action (409 rule codes from the API). */
export const freaksErrorKey = (error) => {
  const code = freaksErrorCode(error);
  return KNOWN_ERRORS.includes(code) ? `theFreaks.errors.${code}` : 'theFreaks.errors.generic';
};

export const isTheFreaksLink = (link) => link === THE_FREAKS_LINK;

/** "4:05" until a date, "0:00" when it has passed. */
export const countdown = (until, now = Date.now()) => {
  if (!until) return '0:00';
  const seconds = Math.max(0, Math.ceil((new Date(until).getTime() - now) / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};

/** 1 234 567 -> "1.2M", 12 345 -> "12.3k" */
export const compactNumber = (value) => {
  if (Math.abs(value) >= 1_000_000) return `${Math.round(value / 100_000) / 10}M`;
  if (Math.abs(value) >= 10_000) return `${Math.round(value / 100) / 10}k`;
  return String(value);
};

/** Translation key for the setting / context name of a Freakland context (settings.contextWof …). */
export const contextNameKey = (context) =>
  `settings.context${context.charAt(0).toUpperCase()}${context.slice(1)}`;

const ENERGY_REGEN_MS = 3 * 60 * 1000;

/** Energy shown between server refreshes: what was loaded plus what has regenerated since. */
export const liveEnergy = (energy, maxEnergy, nextEnergyAt, now = Date.now()) => {
  if (!nextEnergyAt || energy >= maxEnergy) return { energy: Math.min(energy, maxEnergy), next: null };
  const first = new Date(nextEnergyAt).getTime();
  if (now < first) return { energy, next: first };
  const ticks = 1 + Math.floor((now - first) / ENERGY_REGEN_MS);
  const value = Math.min(maxEnergy, energy + ticks);
  return { energy: value, next: value >= maxEnergy ? null : first + ticks * ENERGY_REGEN_MS };
};

/** 0..1 of the way from the current level's respect to the next level's. */
export const levelProgress = (respect = 0, from = 0, to = 0) =>
  (to > from ? Math.min(1, Math.max(0, (respect - from) / (to - from))) : 1);

/** Others see the profile card only when the owner plays and shows it; on my own profile it also invites me to play. */
/** True at the top level (false when the API doesn't send one). */
export const isMaxLevel = (level, maxLevel) => !!maxLevel && (level ?? 0) >= maxLevel;

/**
 * Missions or gear with the locked ones folded away: everything playable or owned, plus the next one to
 * unlock as a teaser. `hidden` is how many are folded.
 */
export const foldLocked = (items) => {
  const isLocked = (item) => !!item.locked && !item.owned;
  const next = items.findIndex(isLocked);
  const visible = items.filter((item, index) => !isLocked(item) || index === next);
  return { visible, hidden: items.length - visible.length };
};

export const showsFreaksCard = (profile) => !!profile?.visible && (!!profile.playing || profile.isMe);

// Same keys and %{param} placeholders as the mobile app (i18n-js), so strings can be copied 1:1.
const DICTIONARIES = {
  en: { ...en, ...freaksEn, settings: { ...en.settings, ...freaksEn.settings } },
  pl: { ...pl, ...freaksPl, settings: { ...pl.settings, ...freaksPl.settings } },
};

const lookup = (dictionary, key) =>
  key.split('.').reduce((node, part) => (node && typeof node === 'object' ? node[part] : undefined), dictionary);

/** t('theFreaks.level', {level: 3}) -> "Lvl 3" in the given language (English fallback, then the key). */
export const freaksTranslate = (language, key, params = {}) => {
  const value = lookup(DICTIONARIES[language] ?? DICTIONARIES.en, key) ?? lookup(DICTIONARIES.en, key);
  if (typeof value !== 'string') return key;
  return value.replace(/%\{(\w+)\}/g, (match, name) => (params[name] !== undefined && params[name] !== null ? String(params[name]) : ''));
};

/** Name of the character stage just reached by an action (null when no new stage, or the context changed). */
export const evolvedInto = (before, after) =>
  (before?.character && after?.character && before.context === after.context && after.character.stage > before.character.stage
    ? after.character.name
    : null);
